/**
 * How a mission travels from the select screen to `/fly`, and back.
 *
 * A mission is fully described by two numbers-and-a-string — the catalog id and
 * the seed — because the generators are pure and deterministic. So the URL
 * carries exactly that, and `/fly` regenerates the identical `MissionSpec` on
 * arrival. No serialized spec in router state, no store to keep in sync: a
 * reload, a bookmark and a shared link all fly the same sortie.
 */

import {
  AIRCRAFT_KEYS,
  FORMATIONS,
  MAX_FLIGHT_AIRCRAFT,
  catalogEntry,
  clampPlayerBombs,
  generateMission,
  MISSION_REGIONS,
  NATIONS,
  SKY_CONDITIONS,
  TIMES_OF_DAY,
  WIND_STRENGTHS,
  type AircraftKey,
  type AmmunitionType,
  type Formation,
  type MissionRegion,
  type MissionSpec,
  type Nation,
  type SkyCondition,
  type TimeOfDay,
  type WindStrength,
} from "@red-opus/engine";
import { browserSettingsStore } from "../audio/settings.ts";
import { applyRealism, currentRealism, type RealismPanel } from "./realism-settings.ts";

/** The original's single missions are flown for the Allies unless the side picker says otherwise. */
export const DEFAULT_NATION: Nation = "allied";

export const MISSION_PARAM = "m";
export const SEED_PARAM = "seed";
export const SIDE_PARAM = "side";
/**
 * Bombs on the player's own aeroplane — a **port extension**
 * (docs/game/player-bombing.md), so it is absent from a URL unless somebody
 * asked for it, exactly like `side`.
 */
export const BOMBS_PARAM = "bombs";
/** Editable Mission Parameters (AD-011) — the boxed briefing values and the Flight Assignments ammunition field. */
export const AIRCRAFT_PARAM = "ac";
export const ENEMY_PARAM = "enemy";
export const REGION_PARAM = "region";
export const AMMO_PARAM = "ammo";
/**
 * Roadmap 11s (Weather Selection) — the three words the original's boxed
 * winds/sky/time-of-day fields open a `CHOOSE` popup on, same shape as the
 * four params above: absent unless the picker was actually opened.
 */
export const WIND_PARAM = "wind";
export const SKY_PARAM = "sky";
export const TIME_PARAM = "time";
/**
 * The two Flight Assignments fields Dogfight a Squadron made real — `FORMATION`
 * and `TOTAL AIRCRAFT IN FLIGHT`. Same shape as every option above: absent from
 * the URL unless the player actually opened the picker, so a link written before
 * they existed still names exactly the sortie it always did.
 */
export const FORMATION_PARAM = "form";
export const FLIGHT_SIZE_PARAM = "flight";

const AMMO_TYPES: readonly AmmunitionType[] = ["regular", "incendiary"];

export interface MissionLaunch {
  readonly catalogId: string;
  readonly seed: number;
  /**
   * Who the player flies for, from the shell's `WHO WILL YOU FLY FOR?` dialog.
   * Absent means the original's own default for a single mission, Allied —
   * and absent is also how the parameter is *left out* of the URL in that
   * case, so every link written before the side picker existed still names
   * exactly the sortie it always did.
   */
  readonly nation?: Nation;
  /** Bombs under the player's own wings. Absent, and absent from the URL, unless asked for. */
  readonly playerBombs?: number;
  /**
   * The four Editable Mission Parameters (AD-011): the manual's boxed briefing
   * values and Flight Assignments ammunition field. Each is absent — and left
   * out of the URL — unless the player actually opened its picker, so every
   * link written before this option existed still names exactly the sortie it
   * always did.
   */
  readonly playerAircraft?: AircraftKey;
  readonly enemyAircraft?: AircraftKey;
  readonly region?: MissionRegion;
  readonly ammunition?: AmmunitionType;
  /**
   * Roadmap 11s — Weather Selection. The same "absent means let the generator
   * draw it" shape as the four fields above, one per boxed weather word the
   * original's Mission Setup sentence offers (`mission-options.ts`'s
   * `windStrength`/`sky`/`timeOfDay`).
   */
  readonly windStrength?: WindStrength;
  readonly sky?: SkyCondition;
  readonly timeOfDay?: TimeOfDay;
  /** `FORMATION` / `TOTAL AIRCRAFT IN FLIGHT`, both absent unless picked. */
  readonly formation?: Formation;
  readonly flightSize?: number;
}

/** The `/fly` URL that flies this mission. */
export function missionLaunchPath(launch: MissionLaunch): string {
  const params = new URLSearchParams({ [MISSION_PARAM]: launch.catalogId, [SEED_PARAM]: String(launch.seed) });
  if (launch.nation && launch.nation !== DEFAULT_NATION) params.set(SIDE_PARAM, launch.nation);
  if (launch.playerBombs) params.set(BOMBS_PARAM, String(clampPlayerBombs(launch.playerBombs)));
  if (launch.playerAircraft) params.set(AIRCRAFT_PARAM, launch.playerAircraft);
  if (launch.enemyAircraft) params.set(ENEMY_PARAM, launch.enemyAircraft);
  if (launch.region) params.set(REGION_PARAM, launch.region);
  if (launch.ammunition) params.set(AMMO_PARAM, launch.ammunition);
  if (launch.windStrength) params.set(WIND_PARAM, launch.windStrength);
  if (launch.sky) params.set(SKY_PARAM, launch.sky);
  if (launch.timeOfDay) params.set(TIME_PARAM, launch.timeOfDay);
  if (launch.formation) params.set(FORMATION_PARAM, launch.formation);
  if (launch.flightSize) params.set(FLIGHT_SIZE_PARAM, String(launch.flightSize));
  return `/fly?${params.toString()}`;
}

/** Anything with a `[key]` lookup — Solid's `useSearchParams` and `URLSearchParams` both qualify. */
export interface SearchLike {
  readonly [key: string]: string | string[] | undefined;
}

function firstValue(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

/**
 * Reads a launch out of a query string. Returns `undefined` — not a default —
 * when there is no mission named, so `/fly` with no parameters can keep meaning
 * "the instant dogfight" rather than silently becoming some mission.
 */
export function parseMissionLaunch(search: SearchLike): MissionLaunch | undefined {
  const catalogId = firstValue(search[MISSION_PARAM]);
  if (!catalogId || !catalogEntry(catalogId)) return undefined;
  const raw = Number.parseInt(firstValue(search[SEED_PARAM]) ?? "", 10);
  const side = NATIONS.find((nation) => nation === firstValue(search[SIDE_PARAM]));
  const seed = Number.isFinite(raw) ? raw >>> 0 : DEFAULT_SEED;
  const bombs = clampPlayerBombs(Number.parseInt(firstValue(search[BOMBS_PARAM]) ?? "", 10));
  const playerAircraft = AIRCRAFT_KEYS.find((key) => key === firstValue(search[AIRCRAFT_PARAM]));
  const enemyAircraft = AIRCRAFT_KEYS.find((key) => key === firstValue(search[ENEMY_PARAM]));
  const region = MISSION_REGIONS.find((entry) => entry === firstValue(search[REGION_PARAM]));
  const ammunition = AMMO_TYPES.find((entry) => entry === firstValue(search[AMMO_PARAM]));
  const windStrength = WIND_STRENGTHS.find((entry) => entry === firstValue(search[WIND_PARAM]));
  const sky = SKY_CONDITIONS.find((entry) => entry === firstValue(search[SKY_PARAM]));
  const timeOfDay = TIMES_OF_DAY.find((entry) => entry === firstValue(search[TIME_PARAM]));
  const formation = FORMATIONS.find((entry) => entry === firstValue(search[FORMATION_PARAM]));
  const rawSize = Number.parseInt(firstValue(search[FLIGHT_SIZE_PARAM]) ?? "", 10);
  const flightSize = Number.isFinite(rawSize) && rawSize >= 1 && rawSize <= MAX_FLIGHT_AIRCRAFT ? rawSize : undefined;
  return {
    catalogId,
    seed,
    ...(side ? { nation: side } : {}),
    // Omitted rather than zeroed, so a launch built from a plain URL is
    // `toEqual`-identical to one built before this parameter existed.
    ...(bombs > 0 ? { playerBombs: bombs } : {}),
    ...(playerAircraft ? { playerAircraft } : {}),
    ...(enemyAircraft ? { enemyAircraft } : {}),
    ...(region ? { region } : {}),
    ...(ammunition ? { ammunition } : {}),
    ...(windStrength ? { windStrength } : {}),
    ...(sky ? { sky } : {}),
    ...(timeOfDay ? { timeOfDay } : {}),
    ...(formation ? { formation } : {}),
    ...(flightSize ? { flightSize } : {}),
  };
}

/** 21 April 1918 — the day Richthofen went down, and this port's default sortie. */
export const DEFAULT_SEED = 19180421;

/**
 * Builds the spec a launch names, or `undefined` if the catalog no longer has
 * it, with the Realism Panel's settings stamped on. That is the whole of how
 * the panel reaches `/fly`: the route asks for a mission, and the mission it
 * gets already carries the player's FLIGHT MODEL and toggles. With no stored
 * panel the stamp is `DEFAULT_REALISM`, which is byte-identical to what the
 * generators set, so an untouched panel changes nothing.
 */
export function missionFor(launch: MissionLaunch, panel?: RealismPanel): MissionSpec | undefined {
  if (!catalogEntry(launch.catalogId)) return undefined;
  const chosen = panel ?? currentRealism(browserSettingsStore());
  const spec = generateMission(launch.catalogId, {
    seed: launch.seed,
    nation: launch.nation ?? DEFAULT_NATION,
    playerBombs: launch.playerBombs,
    aerodromeStart: chosen.aerodromeStart,
    playerAircraft: launch.playerAircraft,
    enemyAircraft: launch.enemyAircraft,
    region: launch.region,
    ammunition: launch.ammunition,
    windStrength: launch.windStrength,
    sky: launch.sky,
    timeOfDay: launch.timeOfDay,
    formation: launch.formation,
    flightSize: launch.flightSize,
  });
  return applyRealism(spec, chosen);
}

/**
 * The next seed for a "roll a different sortie" button. A plain LCG step rather
 * than `Math.random`, so the sequence of missions a player sees from a given
 * starting seed is reproducible and testable — the same reason the sim has no
 * `Math.random` in it either.
 */
export function nextSeed(seed: number): number {
  return (Math.imul(seed >>> 0, 1664525) + 1013904223) >>> 0;
}
